import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';

import { useReducedMotion } from '#hooks/useReducedMotion';

import { claseGavi, colorGavi } from './gaviCss';

/** Gestos de Gavi (acuerdo: «Gavi»). */
export type GaviEstado =
  | 'reposo'
  | 'vuela'
  | 'ladea'
  | 'aplaude'
  | 'ojos-plato'
  | 'tapa'
  | 'eriza';

/** Cuánto dura cada gesto antes de volver a reposo (como la maqueta). */
export const DURACION_GESTO: Record<Exclude<GaviEstado, 'reposo'>, number> = {
  vuela: 800,
  ladea: 1600,
  aplaude: 1400,
  'ojos-plato': 1600,
  tapa: 2200,
  eriza: 1200,
};

/** Clase CSS de cada estado (`ojos-plato` es `platos` en la maqueta). */
export function claseEstadoGavi(estado: GaviEstado): string {
  if (estado === 'reposo') {
    return '';
  }
  return estado === 'ojos-plato' ? 'platos' : estado;
}

type GaviProps = {
  /** Gesto que hace ahora. Por defecto, reposo (balanceo y parpadeo). */
  estado?: GaviEstado;
  /**
   * Cambia este valor para repetir el gesto aunque `estado` no cambie (por
   * ejemplo, otro aplauso). Cada cambio reinicia la animación.
   */
  clave?: string | number;
  /**
   * Milisegundos hasta volver a reposo. Por defecto, `DURACION_GESTO`;
   * `null` = mantiene el gesto hasta que cambie `estado`.
   */
  duracion?: number | null;
  /** Ancho en px (alto proporcional, 56 × 52 por defecto). */
  ancho?: number;
  className?: string;
  style?: CSSProperties;
};

/**
 * Gavi, la gaviota del puerto: dibujo propio en SVG plano (copia fiel de la
 * maqueta). Quieta con «reducir movimiento»: no se balancea, no parpadea ni
 * vuela, pero los gestos fijos (ladear, taparse los ojos…) sí se ven.
 */
export function Gavi({
  estado = 'reposo',
  clave,
  duracion,
  ancho = 56,
  className,
  style,
}: GaviProps) {
  const quieta = useReducedMotion();
  // Gesto ya terminado (vuelve a reposo): se recuerda por su ficha, así un
  // gesto nuevo (o el mismo con otra `clave`) vuelve a verse.
  const ficha = `${estado}|${clave ?? ''}|${duracion ?? ''}`;
  const [terminado, setTerminado] = useState<string | null>(null);

  useEffect(() => {
    if (estado === 'reposo') {
      return;
    }
    const ms = duracion === undefined ? DURACION_GESTO[estado] : duracion;
    if (ms === null) {
      return;
    }
    const id = window.setTimeout(() => setTerminado(ficha), ms);
    return () => window.clearTimeout(id);
  }, [estado, duracion, ficha]);

  const actual: GaviEstado = terminado === ficha ? 'reposo' : estado;
  const clases = ['gavi', claseGavi, claseEstadoGavi(actual)];
  if (quieta) {
    clases.push('quieta');
  }
  if (className) {
    clases.push(className);
  }

  return (
    <span
      key={clave}
      className={clases.filter(Boolean).join(' ')}
      data-testid="gavi"
      data-estado={actual}
      aria-hidden
      style={{ width: ancho, height: (ancho * 52) / 56, ...style }}
    >
      <svg viewBox="0 0 64 60" aria-hidden>
        <g className="cuerpo-g">
          <path d="M8 40 L1 44 L10 45 Z" fill={colorGavi.gris} />
          <ellipse
            cx="28"
            cy="40"
            rx="19"
            ry="13"
            fill={colorGavi.blanco}
            stroke={colorGavi.borde}
          />
          <g
            className="plumas"
            fill={colorGavi.blanco}
            stroke={colorGavi.borde}
          >
            <path d="M14 30 L12 22 L19 29 Z" />
            <path d="M22 27 L22 18 L27 27 Z" />
            <path d="M32 28 L35 20 L36 29 Z" />
          </g>
          <path
            d="M26 53 L24 59 M33 53 L35 59"
            stroke={colorGavi.pico}
            strokeWidth="2.6"
            strokeLinecap="round"
          />
          <g className="cabeza">
            <circle
              cx="42"
              cy="24"
              r="11"
              fill={colorGavi.blanco}
              stroke={colorGavi.borde}
            />
            <g className="ojo">
              <circle cx="45.5" cy="21.5" r="2.3" fill={colorGavi.ojo} />
              <circle cx="46.2" cy="20.8" r=".7" fill={colorGavi.brillo} />
            </g>
            <path d="M51 25 L61 27.5 L51 30 Z" fill={colorGavi.pico} />
            <circle cx="57" cy="28.4" r="1.1" fill={colorGavi.moflete} />
          </g>
          <g className="ala">
            <path
              d="M12 38 Q22 25 40 33 Q31 38 21 47 Z"
              fill={colorGavi.gris}
            />
            <path d="M12 38 L8 34 L16 35 Z" fill={colorGavi.punta} />
          </g>
        </g>
      </svg>
    </span>
  );
}
